<template>
  <v-container fluid class="page">
    <div class="page-hero">
      <div>
        <h2>Configuración de Templates de Remitos</h2>
        <p>Personalice, visualice y administre los templates de remito por empresa.</p>
      </div>
    </div>

    <!-- Mensajes -->
    <v-alert v-if="successMessage" type="success" dense outlined class="mb-4">
      {{ successMessage }}
    </v-alert>
    <v-alert v-if="errorMessage" type="error" dense outlined class="mb-4">
      {{ errorMessage }}
    </v-alert>

    <!-- Selector de Empresa -->
    <v-card class="page-card mb-4 company-card">
      <v-row dense class="align-center">
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="empresaSeleccionada"
            :items="empresas"
            item-text="label"
            item-value="value"
            label="Seleccionar Empresa"
            prepend-icon="mdi-domain"
            outlined
            dense
            hide-details
            clearable
            @change="onEmpresaChange"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model.number="manualCompanyId"
            label="O ingresar ID Empresa"
            type="number"
            outlined
            dense
            hide-details
            append-outer-icon="mdi-arrow-right-bold-circle-outline"
            @click:append-outer="setManualCompany"
            @keyup.enter="setManualCompany"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2" v-if="empresaSeleccionada">
          <v-select
            v-model="templateSeleccionado"
            :items="templates"
            item-text="Nombre"
            item-value="Id"
            label="Template"
            outlined
            dense
            hide-details
            @change="cargarTemplate"
          >
            <template v-slot:append-outer>
              <v-btn
                color="primary"
                icon
                small
                @click="dialogNuevoTemplate = true"
                title="Crear nuevo template"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
          </v-select>
        </v-col>
      </v-row>
    </v-card>

    <!-- Upload de Logo -->
    <v-row v-if="empresaSeleccionada">
      <v-col cols="12" md="12" class="mb-4">
        <logo-uploader 
          :idEmpresa="empresaSeleccionada"
          @logo-uploaded="onLogoUploaded"
        />
      </v-col>
    </v-row>

    <!-- Editor de Template -->
    <v-row v-if="template">
      <v-col cols="12" lg="8">
        <v-card class="page-card mb-4 pa-4">
          <v-row dense class="align-center">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="template.Nombre"
                label="Nombre del Template"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="template.TipoDocumento"
                :items="[
                  { text: 'Remito', value: 'REMITO' },
                  { text: 'Remito interno (REM-X)', value: 'REM_X' },
                  { text: 'Factura A', value: 'FACTURA_A' },
                  { text: 'Factura B', value: 'FACTURA_B' },
                  { text: 'Factura C', value: 'FACTURA_C' }
                ]"
                item-text="text"
                item-value="value"
                label="Tipo de Documento"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
          </v-row>
        </v-card>
        <v-card class="page-card">
          <v-tabs v-slot:default v-model="tabActiva" background-color="primary" dark class="rounded-t-lg">
            <v-tab><v-icon left>mdi-table-column</v-icon> Columnas</v-tab>
            <v-tab><v-icon left>mdi-form-textbox</v-icon> Campos</v-tab>
            <v-tab><v-icon left>mdi-office-building</v-icon> Datos Empresa</v-tab>
            <v-tab><v-icon left>mdi-palette</v-icon> Estilos</v-tab>
            <v-tab><v-icon left>mdi-text</v-icon> Textos</v-tab>
            <v-tab><v-icon left>mdi-view-dashboard</v-icon> Layout</v-tab>
            <v-tab><v-icon left>mdi-cog</v-icon> Avanzado</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tabActiva" class="pa-4">
            <!-- Tab Columnas -->
            <v-tab-item>
              <columnas-editor
                v-if="template.TablaConfig"
                :columnas="template.TablaConfig.columnas"
                :tamano-datos-tabla="template.EstilosConfig && template.EstilosConfig.tabla ? template.EstilosConfig.tabla.tamanoDatos : ''"
                @update="actualizarColumnas"
                @update-tamano-datos="actualizarTamanoDatosTabla"
              />
            </v-tab-item>

            <!-- Tab Campos -->
            <v-tab-item>
              <campos-editor
                v-if="template.CamposConfig"
                :campos-config="template.CamposConfig"
                @update="actualizarCampos"
              />
            </v-tab-item>

            <!-- Tab Datos Empresa -->
            <v-tab-item>
              <datos-empresa-editor
                v-if="template.EstilosConfig && template.EstilosConfig.datosEmpresa"
                :datos-empresa="template.EstilosConfig.datosEmpresa"
                @update="actualizarDatosEmpresa"
                @recargar-datos="recargarDatosBD"
              />
            </v-tab-item>

            <!-- Tab Estilos -->
            <v-tab-item>
              <estilos-editor
                v-if="template.EstilosConfig"
                :estilos="template.EstilosConfig"
                @update="actualizarEstilos"
              />
            </v-tab-item>

            <!-- Tab Textos -->
            <v-tab-item>
              <textos-editor
                v-if="template.TextosConfig"
                :textos="template.TextosConfig"
                @update="actualizarTextos"
              />
            </v-tab-item>

            <!-- Tab Layout -->
            <v-tab-item>
              <layout-editor
                v-if="template.LayoutConfig"
                :layout="template.LayoutConfig"
                @update="actualizarLayout"
              />
            </v-tab-item>

            <!-- Tab Avanzado -->
            <v-tab-item>
              <opciones-avanzadas-editor
                v-if="template.OpcionesAvanzadas"
                :opciones="template.OpcionesAvanzadas"
                @update="actualizarOpcionesAvanzadas"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <!-- Botones de Acción -->
        <div class="mt-4">
          <v-btn
            color="success"
            large
            @click="guardarCambios"
            :loading="guardando"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar Cambios
          </v-btn>
          <v-btn
            color="info"
            large
            class="ml-2"
            @click="duplicarTemplate"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Duplicar
          </v-btn>
          <v-btn
            color="error"
            large
            class="ml-2"
            @click="eliminarTemplate"
            :disabled="template.EsDefault"
          >
            <v-icon left>mdi-delete</v-icon>
            Eliminar
          </v-btn>
        </div>
      </v-col>

      <!-- Vista Previa -->
      <v-col cols="12" lg="4">
        <v-card class="page-card flex-column">
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-eye</v-icon>
            Vista Previa
          </v-card-title>
          <v-card-text class="pa-2">
            <iframe
              v-if="previewUrl"
              :src="previewUrl"
              style="width: 100%; height: 750px; border: 1px solid #dbe4ef; border-radius: 12px; background-color: white;"
              @load="previewCargado = true"
            ></iframe>
            <v-progress-linear
              v-if="!previewCargado"
              indeterminate
              color="primary"
            ></v-progress-linear>
          </v-card-text>
          <v-card-actions class="flex-column pa-2">
            <v-btn
              color="primary"
              block
              @click="actualizarPreview"
              class="mb-2 font-weight-bold"
            >
              <v-icon left>mdi-refresh</v-icon>
              Actualizar Vista Previa
            </v-btn>
            <v-btn
              color="success"
              block
              @click="generarPDFRemito"
              :disabled="!template || !template.Id"
              class="mb-2 font-weight-bold"
            >
              <v-icon left>mdi-file-pdf-box</v-icon>
              Ver PDF Completo
            </v-btn>
            <v-btn
              color="info"
              block
              @click="imprimirRemito"
              :disabled="!template || !template.Id"
              class="font-weight-bold"
            >
              <v-icon left>mdi-printer</v-icon>
              Imprimir Remito
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog Nuevo Template -->
    <v-dialog v-model="dialogNuevoTemplate" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Crear Nuevo Template</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="nuevoTemplateNombre"
            label="Nombre del Template"
            outlined
            dense
            hide-details
            class="mb-2"
          ></v-text-field>
          <v-select
            v-model="nuevoTemplateTipo"
            :items="[
              { text: 'Remito', value: 'REMITO' },
              { text: 'Factura A', value: 'FACTURA_A' },
              { text: 'Factura B', value: 'FACTURA_B' },
              { text: 'Factura C', value: 'FACTURA_C' }
            ]"
            item-text="text"
            item-value="value"
            label="Tipo de Documento"
            outlined
            dense
            hide-details
            class="mb-2"
          ></v-select>
          <v-switch
            v-model="nuevoTemplateActivo"
            label="Activar template"
            hide-details
          ></v-switch>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogNuevoTemplate = false">Cancelar</v-btn>
          <v-btn color="primary" @click="crearNuevoTemplate">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Duplicar Template -->
    <v-dialog v-model="dialogDuplicar" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Duplicar Template</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="nombreDuplicado"
            label="Nombre del nuevo template"
            outlined
            dense
            hide-details
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDuplicar = false">Cancelar</v-btn>
          <v-btn color="primary" @click="confirmarDuplicar">Duplicar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ColumnasEditor from "../../components/RemitoTemplates/ColumnasEditor.vue";
import CamposEditor from "../../components/RemitoTemplates/CamposEditor.vue";
import EstilosEditor from "../../components/RemitoTemplates/EstilosEditor.vue";
import TextosEditor from "../../components/RemitoTemplates/TextosEditor.vue";
import LayoutEditor from "../../components/RemitoTemplates/LayoutEditor.vue";
import OpcionesAvanzadasEditor from "../../components/RemitoTemplates/OpcionesAvanzadasEditor.vue";
import LogoUploader from "../../components/RemitoTemplates/LogoUploader.vue";
import DatosEmpresaEditor from "../../components/RemitoTemplates/DatosEmpresaEditor.vue";
import api from "../../services/api";

const STORAGE_KEY = "herra.wmsCompanies";

export default {
  name: "ConfiguracionRemitos",
  
  components: {
    ColumnasEditor,
    CamposEditor,
    EstilosEditor,
    TextosEditor,
    LayoutEditor,
    OpcionesAvanzadasEditor,
    LogoUploader,
    DatosEmpresaEditor
  },

  data() {
    return {
      empresas: [],
      empresaSeleccionada: null,
      manualCompanyId: null,
      templateSeleccionado: null,
      template: null,
      templates: [],
      tabActiva: 0,
      guardando: false,
      previewCargado: false,
      previewUrl: null,
      dialogNuevoTemplate: false,
      nuevoTemplateNombre: "",
      nuevoTemplateActivo: true,
      nuevoTemplateTipo: "REMITO",
      dialogDuplicar: false,
      nombreDuplicado: "",
      successMessage: "",
      errorMessage: ""
    }
  },

  async mounted() {
    await this.cargarEmpresas();
  },

  methods: {
    async cargarEmpresas() {
      try {
        let parsed = [];
        try {
          const res = await api.get("/wms/companies");
          if (res.data && res.data.data) {
            parsed = res.data.data;
          }
        } catch (err) {
          console.warn("No se pudo obtener empresas del backend, usando fallback de localStorage");
          const raw = localStorage.getItem(STORAGE_KEY);
          parsed = raw ? JSON.parse(raw) : [];
        }

        this.empresas = (Array.isArray(parsed) ? parsed : []).map(emp => ({
          value: emp.id,
          label: [emp.code, emp.name].filter(Boolean).join(" - ")
        }));
        if (!this.empresaSeleccionada && this.empresas.length === 1) {
          this.empresaSeleccionada = this.empresas[0].value;
          this.cargarTemplates();
        }
      } catch (error) {
        console.error("Error al cargar empresas:", error);
        this.errorMessage = "Error al cargar empresas";
      }
    },

    setManualCompany() {
      if (!this.manualCompanyId) return;
      this.empresaSeleccionada = this.manualCompanyId;
      this.cargarTemplates();
    },

    onEmpresaChange() {
      this.manualCompanyId = null;
      this.cargarTemplates();
    },

    async cargarTemplates() {
      if (!this.empresaSeleccionada) return;
      
      try {
        this.errorMessage = "";
        this.successMessage = "";
        this.templates = await this.$store.dispatch(
          "remitoTemplates/listarTemplates",
          this.empresaSeleccionada
        );
        
        // Seleccionar el template activo por defecto
        const activo = this.templates.find(t => t.Activo);
        if (activo) {
          this.templateSeleccionado = activo.Id;
          await this.cargarTemplate();
        } else if (this.templates.length > 0) {
          this.templateSeleccionado = this.templates[0].Id;
          await this.cargarTemplate();
        }
      } catch (error) {
        console.error("Error al cargar templates:", error);
        this.errorMessage = "Error al cargar templates de la empresa";
      }
    },

    async cargarTemplate() {
      if (!this.templateSeleccionado) return;
      this.previewCargado = false;
      this.errorMessage = "";
      this.successMessage = "";
      
      try {
        const resultado = await this.$store.dispatch(
          "remitoTemplates/getTemplateById",
          this.templateSeleccionado
        );
        
        this.template = resultado.data || resultado;
        
        // Inicializar datosEmpresa dentro de EstilosConfig si no existe
        if (!this.template.EstilosConfig) {
          this.$set(this.template, "EstilosConfig", {});
        }
        if (!this.template.EstilosConfig.datosEmpresa) {
          this.$set(this.template.EstilosConfig, "datosEmpresa", {
            razonSocial: { visible: true, valor: "", formato: "bold" },
            direccion: { visible: true, valor: "", formato: "normal" },
            localidad: { visible: true, valor: "", formato: "normal" },
            telefono: { visible: true, valor: "", formato: "normal" },
            web: { visible: true, valor: "", formato: "normal" },
            cuit: { visible: true, valor: "", formato: "normal" },
            inicioActividades: { visible: true, valor: "", formato: "normal" },
            ingresosBrutos: { visible: true, valor: "", formato: "normal" },
            email: { visible: true, valor: "", formato: "normal" },
            estiloEncabezado: {
              alineacion: "left",
              tamanoFuente: "12px",
              tamanoFuenteRazonSocial: "16px",
              peso: "normal"
            }
          });
        }

        if (!this.template.EstilosConfig.tabla) {
          this.$set(this.template.EstilosConfig, "tabla", {
            tamanoDatos: "9px"
          });
        } else if (!this.template.EstilosConfig.tabla.tamanoDatos) {
          this.$set(this.template.EstilosConfig.tabla, "tamanoDatos", "9px");
        }
        
        // Prellenar con datos vacíos si es el caso
        await this.prellenarDatosVacios();
        
        // Cargar vista previa
        this.actualizarPreview();
      } catch (error) {
        console.error("Error al cargar template:", error);
        this.errorMessage = "Error al cargar template";
      }
    },

    async prellenarDatosVacios() {
      if (!this.template || !this.empresaSeleccionada) return;
      
      try {
        const datosEmpresa = this.template.EstilosConfig.datosEmpresa;
        const hayDatosVacios = Object.keys(datosEmpresa).some(
          key => key !== "estiloEncabezado" && datosEmpresa[key] && datosEmpresa[key].valor === ""
        );
        
        if (hayDatosVacios) {
          console.log("[ConfiguracionRemitos] Prellenando datos vacíos desde BD...");
          const datosReales = await this.$store.dispatch(
            "remitoTemplates/getDatosEmpresa",
            this.empresaSeleccionada
          );
          
          Object.keys(datosReales).forEach(key => {
            if (datosEmpresa[key] && datosEmpresa[key].valor === "") {
              datosEmpresa[key].valor = datosReales[key].valor;
            }
          });
          this.successMessage = "Información de empresa actualizada desde la base de datos";
        }
      } catch (error) {
        console.error("[ConfiguracionRemitos] Error al prellenar datos:", error);
      }
    },

    async recargarDatosBD() {
      if (!this.template || !this.empresaSeleccionada) return;
      this.errorMessage = "";
      this.successMessage = "";
      
      try {
        const datosReales = await this.$store.dispatch(
          "remitoTemplates/getDatosEmpresa",
          this.empresaSeleccionada
        );
        
        const datosEmpresa = this.template.EstilosConfig.datosEmpresa;
        Object.keys(datosReales).forEach(key => {
          if (datosEmpresa[key]) {
            datosEmpresa[key].valor = datosReales[key].valor;
          }
        });
        
        this.successMessage = "Datos de empresa recargados correctamente";
        this.actualizarPreview();
      } catch (error) {
        console.error("[ConfiguracionRemitos] Error al recargar datos:", error);
        this.errorMessage = "Error al obtener datos reales de la empresa";
      }
    },

    actualizarColumnas(columnas) {
      if (this.template && this.template.TablaConfig) {
        this.template.TablaConfig.columnas = columnas;
      }
    },

    actualizarTamanoDatosTabla(tamanoDatos) {
      if (this.template && this.template.EstilosConfig) {
        if (!this.template.EstilosConfig.tabla) {
          this.$set(this.template.EstilosConfig, "tabla", {});
        }
        this.$set(this.template.EstilosConfig.tabla, "tamanoDatos", tamanoDatos || "9px");
      }
    },

    actualizarCampos(nuevosCampos) {
      if (this.template) {
        this.template.CamposConfig = nuevosCampos;
      }
    },

    actualizarDatosEmpresa(nuevosDatos) {
      if (this.template && this.template.EstilosConfig) {
        this.template.EstilosConfig.datosEmpresa = nuevosDatos;
      }
    },

    actualizarEstilos(estilos) {
      if (this.template) {
        this.template.EstilosConfig = estilos;
      }
    },

    actualizarTextos(textos) {
      if (this.template) {
        this.template.TextosConfig = textos;
      }
    },

    actualizarLayout(layout) {
      if (this.template) {
        this.template.LayoutConfig = layout;
      }
    },

    actualizarOpcionesAvanzadas(opciones) {
      if (this.template) {
        this.template.OpcionesAvanzadas = opciones;
      }
    },

    async guardarCambios() {
      this.guardando = true;
      this.errorMessage = "";
      this.successMessage = "";
      
      try {
        const templateActualizado = await this.$store.dispatch("remitoTemplates/actualizarTemplate", {
          id: this.template.Id,
          payload: this.template
        });

        if (templateActualizado && typeof templateActualizado === "object") {
          this.template = templateActualizado;
        }
        
        this.successMessage = "Template guardado correctamente";
        this.actualizarPreview();
      } catch (error) {
        console.error("Error al guardar template:", error);
        this.errorMessage = "Error al guardar template";
      } finally {
        this.guardando = false;
      }
    },

    async crearNuevoTemplate() {
      if (!this.nuevoTemplateNombre) {
        this.errorMessage = "Ingrese un nombre para el template";
        return;
      }

      this.errorMessage = "";
      this.successMessage = "";

      try {
        const configDefault = await this.$store.dispatch(
          "remitoTemplates/getConfigDefault"
        );

        const nuevoTemplate = {
          IdEmpresa: this.empresaSeleccionada,
          Nombre: this.nuevoTemplateNombre,
          Activo: this.nuevoTemplateActivo,
          EsDefault: false,
          TipoDocumento: this.nuevoTemplateTipo,
          ...configDefault
        };

        const creado = await this.$store.dispatch(
          "remitoTemplates/crearTemplate",
          nuevoTemplate
        );

        this.successMessage = "Template creado correctamente";
        this.dialogNuevoTemplate = false;
        this.nuevoTemplateNombre = "";
        
        // Recargar templates
        await this.cargarTemplates();
        this.templateSeleccionado = creado.Id;
        await this.cargarTemplate();
      } catch (error) {
        console.error("Error al crear template:", error);
        this.errorMessage = "Error al crear template";
      }
    },

    duplicarTemplate() {
      this.nombreDuplicado = `${this.template.Nombre} (Copia)`;
      this.dialogDuplicar = true;
    },

    async confirmarDuplicar() {
      if (!this.nombreDuplicado) {
        this.errorMessage = "Ingrese un nombre para el template";
        return;
      }

      this.errorMessage = "";
      this.successMessage = "";

      try {
        const duplicado = await this.$store.dispatch(
          "remitoTemplates/duplicarTemplate",
          {
            id: this.template.Id,
            nuevoNombre: this.nombreDuplicado
          }
        );

        this.successMessage = "Template duplicado correctamente";
        this.dialogDuplicar = false;
        
        // Recargar templates
        await this.cargarTemplates();
        this.templateSeleccionado = duplicado.Id;
        await this.cargarTemplate();
      } catch (error) {
        console.error("Error al duplicar template:", error);
        this.errorMessage = "Error al duplicar template";
      }
    },

    async eliminarTemplate() {
      if (this.template.EsDefault) return;

      const confirmar = confirm("¿Está seguro que desea eliminar este template?");
      if (!confirmar) return;

      this.errorMessage = "";
      this.successMessage = "";

      try {
        await this.$store.dispatch(
          "remitoTemplates/eliminarTemplate",
          this.template.Id
        );

        this.successMessage = "Template eliminado correctamente";
        this.template = null;
        this.templateSeleccionado = null;
        await this.cargarTemplates();
      } catch (error) {
        console.error("Error al eliminar template:", error);
        this.errorMessage = "Error al eliminar template";
      }
    },

    actualizarPreview() {
      if (!this.template || !this.template.Id) {
        console.warn("[ConfiguracionRemitos] No hay template para previsualizar");
        return;
      }
      
      this.previewCargado = false;
      const timestamp = Date.now();
      const token = this.$store.state.auth.accessToken || "";
      const baseUrl = api.defaults.baseURL || window.location.origin + "/apiv3";
      
      this.previewUrl = `${baseUrl}/remito-templates/${this.template.Id}/preview?token=${encodeURIComponent(token)}&t=${timestamp}`;
      console.log("[ConfiguracionRemitos] Preview URL:", this.previewUrl);
      
      setTimeout(() => {
        this.previewCargado = true;
      }, 100);
    },

    async onLogoUploaded(result) {
      console.log("[ConfiguracionRemitos] Logo subido:", result);
      
      if (this.template && this.template.EstilosConfig) {
        const logoUrl = result.url || `/uploads/logos/${this.empresaSeleccionada}/logo.png`;
        this.$set(this.template.EstilosConfig.logo, "url", logoUrl);
        
        try {
          await this.$store.dispatch("remitoTemplates/actualizarTemplate", {
            id: this.template.Id,
            payload: this.template
          });
          
          this.successMessage = "Logo subido y configurado en el template";
          
          setTimeout(() => {
            this.actualizarPreview();
          }, 1000);
        } catch (error) {
          console.error("[ConfiguracionRemitos] Error al actualizar template con logo:", error);
          this.errorMessage = "Error al asociar el logo al template";
        }
      }
    },

    generarPDFRemito() {
      if (!this.template || !this.template.Id) {
        this.errorMessage = "Seleccione un template primero";
        return;
      }

      const token = this.$store.state.auth.accessToken || "";
      const baseUrl = api.defaults.baseURL || window.location.origin + "/apiv3";
      const pdfUrl = `${baseUrl}/remito-templates/${this.template.Id}/preview?format=pdf&token=${encodeURIComponent(token)}&t=${Date.now()}`;
      window.open(pdfUrl, "_blank");
    },

    imprimirRemito() {
      if (!this.template || !this.template.Id) {
        this.errorMessage = "Seleccione un template primero";
        return;
      }

      const token = this.$store.state.auth.accessToken || "";
      const baseUrl = api.defaults.baseURL || window.location.origin + "/apiv3";
      const pdfUrl = `${baseUrl}/remito-templates/${this.template.Id}/preview?format=pdf&token=${encodeURIComponent(token)}&t=${Date.now()}`;
      const printWindow = window.open(pdfUrl, "_blank");
      
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }
    }
  }
}
</script>

<style scoped>
.page {
  padding: 8px 6px 24px;
  background: transparent;
  min-height: 100%;
}

.page-hero {
  background: var(--bg-card, #ffffff);
  border: 1px solid var(--border-subtle, #e2e8f0);
  border-radius: 18px;
  padding: 20px 24px;
  margin-bottom: 16px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.page-hero h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
}

.page-hero p {
  margin: 0;
  color: #64748b;
}

.page-card {
  border-radius: 18px;
  padding: 16px 20px;
  border: 1px solid var(--border-subtle, #e2e8f0) !important;
  background: var(--bg-card, #ffffff);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
}

.company-card {
  border-left: 4px solid #3b82f6 !important;
}

.flex-column {
  display: flex;
  flex-direction: column;
}
</style>
